/**
 * The recorder wire protocol, PLAN.md §7. Shared verbatim by the browser
 * uploader and the ingest service so the two can never drift apart.
 *
 * Chunks travel as binary frames — a JSON envelope per 3-second blob would add
 * base64 overhead to the only message that is ever hot:
 *
 *   byte 0      frame type (1=chunk, 2=finalize, 3=heartbeat)
 *   bytes 1-4   seq, uint32 LE
 *   bytes 5-12  client timestamp ms, float64 LE
 *   bytes 13+   opus/aac payload
 *
 * Everything off the hot path — the handshake, acks, errors — is JSON text
 * frames, typed below.
 */

export const FRAME_HEADER_BYTES = 13;

export const FrameType = {
  Chunk: 1,
  Finalize: 2,
  Heartbeat: 3,
} as const;

export type FrameType = (typeof FrameType)[keyof typeof FrameType];

export interface Frame {
  type: FrameType;
  seq: number;
  /** `Date.now()` on the client when the frame was built. */
  clientTs: number;
  payload: Uint8Array;
}

export function encodeFrame(
  type: FrameType,
  seq: number,
  clientTs: number,
  payload: Uint8Array = new Uint8Array(0),
): Uint8Array {
  const out = new Uint8Array(FRAME_HEADER_BYTES + payload.byteLength);
  const view = new DataView(out.buffer);
  view.setUint8(0, type);
  view.setUint32(1, seq, true);
  view.setFloat64(5, clientTs, true);
  out.set(payload, FRAME_HEADER_BYTES);
  return out;
}

/** Throws on a frame too short to hold a header or carrying an unknown type. */
export function decodeFrame(input: ArrayBuffer | Uint8Array): Frame {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.byteLength < FRAME_HEADER_BYTES) {
    throw new Error(`frame too short: ${bytes.byteLength} bytes`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const type = view.getUint8(0);
  if (type !== FrameType.Chunk && type !== FrameType.Finalize && type !== FrameType.Heartbeat) {
    throw new Error(`unknown frame type: ${type}`);
  }
  return {
    type,
    seq: view.getUint32(1, true),
    clientTs: view.getFloat64(5, true),
    // Copied, not a view: the caller usually outlives the socket's buffer.
    payload: bytes.slice(FRAME_HEADER_BYTES),
  };
}

/** First message on every socket, before any binary frame. */
export interface HelloMessage {
  type: "hello";
  recordingId: string;
  /** Short-lived HMAC from `recordings.issueIngestToken`. */
  token: string;
  /** The container `MediaRecorder` actually produced, e.g. `audio/webm`. */
  mimeType: string;
}

export type ClientMessage = HelloMessage;

/**
 * Server → client. `resume` answers the hello and is what makes a reconnect
 * cheap: the client only re-sends what the server does not already hold.
 */
export type ServerMessage =
  | { type: "resume"; recordingId: string; lastAckedSeq: number; missingSeqs: number[] }
  | { type: "ack"; seq: number }
  | { type: "finalized"; bytes: number; chunkCount: number; gapSeqs: number[] }
  | { type: "error"; code: ServerErrorCode; message: string };

export type ServerErrorCode =
  | "bad_hello"
  | "bad_token"
  | "expired_token"
  | "unknown_recording"
  | "already_finalized"
  | "storage_failed"
  | "internal";

/** Heartbeat cadence, both directions. nginx keeps the socket open for 1h. */
export const HEARTBEAT_MS = 15_000;

/** One `MediaRecorder` blob per this many ms. */
export const CHUNK_INTERVAL_MS = 3_000;

/** How often ingest persists `lastAckedSeq` to Convex — PLAN.md §7. */
export const PROGRESS_EVERY_CHUNKS = 10;
